import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { api } from '../../config';

export interface Release {
  id: number;
  version: string;
  channel: 'stable' | 'beta' | 'alpha';
  platform: 'win32' | 'darwin' | 'linux';
  architecture: 'x64' | 'arm64' | 'ia32';
  fileUrl: string;
  fileSize: number;
  sha512: string;
  blockMapUrl?: string;
  blockMapSize?: number;
  releaseDate: string;
  releaseNotes?: string;
  minimumAppVersion?: string;
  isMandatory: boolean;
  isActive: boolean;
  isDraft: boolean;
  rolloutPercentage: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReleaseAnalytics {
  releaseStats: Array<{
    id: number;
    version: string;
    channel: string;
    platform: string;
    architecture: string;
    releaseDate: string;
    uniqueDownloads: number;
    totalDownloads: number;
    completedDownloads: number;
    failedDownloads: number;
  }>;
  machineActivity: Array<{
    displayId: string;
    platform: string;
    architecture: string;
    lastVersion: string;
    lastSeen: string;
    associatedLicenses: number;
    totalEvents: number;
    lastEvent: string;
  }>;
  updateTrends: Array<{
    date: string;
    totalChecks: number;
    uniqueMachines: number;
    updatesAvailable: number;
  }>;
}

export function useReleases() {
  return useQuery<Release[]>({
    queryKey: ['releases'],
    queryFn: async () => {
      return await api.get<Release[]>('/api/v1/updates/admin/releases?includeDrafts=true');
    },
  });
}

export function useReleaseAnalytics() {
  return useQuery<ReleaseAnalytics>({
    queryKey: ['releaseAnalytics'],
    queryFn: async () => {
      return await api.get<ReleaseAnalytics>('/api/v1/updates/admin/analytics');
    },
  });
}

export interface DeletedRelease {
  id: number;
  version: string;
  channel: string;
  platform: string;
  architecture: string;
  fileSize: number;
  deletedAt: string;
  deletedBy: { id: string; email: string; name: string } | null;
  deletionReason?: string;
  daysUntilPermanentDeletion: number;
  deleteUserEmail?: string;
}

export interface TrashResponse {
  releases: DeletedRelease[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface UseDeletedReleasesOptions {
  page?: number;
  pageSize?: number;
  search?: string;
}

export function useDeletedReleases(
  options: UseDeletedReleasesOptions = {},
  queryOptions?: Omit<UseQueryOptions<TrashResponse>, 'queryKey' | 'queryFn'>
) {
  const { page = 1, pageSize = 20, search } = options;
  
  return useQuery<TrashResponse>({
    queryKey: ['releases', 'trash', page, pageSize, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (search) {
        params.append('search', search);
      }
      return await api.get<TrashResponse>(`/api/v1/updates/admin/releases/trash?${params}`);
    },
    ...queryOptions,
  });
}